import type { IsAuthorizedResponse, LoginRequest, PasswordResetConfirmRequest, RegisterRequest } from "@/types/api";

import { API_ERROR_MESSAGES, ApiError, isApiError } from "./ApiError";
import * as authApi from "./auth";
import {
    connectAuth,
    notifySessionEnded,
    onSessionEnded,
    setAuthStatus,
    type AuthBridge,
    type AuthStatus,
} from "./authBridge";
import { withRefreshLock } from "./crossTabLock";

/**
 * Сессия вкладки — модуль-синглтон вне React (`docs/auth.md`). Access-токен живёт только в памяти модуля;
 * refresh-токен — в HttpOnly-cookie, которую браузер шлёт только на `/api/v1/isauthorized`.
 *
 * - `startAuthSession()` — первый `isauthorized`; до его ответа `authReady` не разрешён, статус `unknown`.
 * - Любой `isauthorized` (старт, refresh по 401, проактивный) — single-flight во вкладке и под блокировкой
 *   между вкладками (`withRefreshLock`): запрос ротирует cookie.
 * - `BroadcastChannel('mado-auth')`: `session-changed`, `token-updated` и `logout`, без токенов.
 */

export interface AuthUser {
    email: string;
    isSuperuser: boolean;
    userId: number;
    displayName: string;
}

/** Почему сессия закончилась: после выхода — на вход без возврата, иначе — с возвратом на текущий адрес. */
export type SessionEndReason = "logout" | "expired";

export interface AuthSnapshot {
    status: AuthStatus;
    user: AuthUser | null;
    /** Первый `isauthorized` не получил ответа (сеть, 5xx): экран «Сервер недоступен» с повтором, а не вход. */
    startupError: ApiError | null;
    endReason: SessionEndReason | null;
}

export type AuthTabMessage = { type: "token-updated" | "session-changed" | "logout"; at: number };

export const AUTH_CHANNEL_NAME = "mado-auth";
const AUTH_STORAGE_EVENT = "mado-auth-event";
/** Проактивный refresh — за минуту до `accessExpiresAt`. */
export const REFRESH_BEFORE_EXPIRY_MS = 60_000;
/**
 * Меньшая задержка означает расхождение часов клиента и сервера (или очень короткий токен):
 * проактивный refresh не планируется, токен обновит refresh по 401.
 */
export const MIN_PROACTIVE_REFRESH_DELAY_MS = 30_000;
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

const INITIAL_SNAPSHOT: AuthSnapshot = { status: "unknown", user: null, startupError: null, endReason: null };

let accessToken: string | null = null;
/** Растёт при каждой смене токена во вкладке: ответ `isauthorized`, начатого до смены, устарел. */
let tokenVersion = 0;
let contextVersion = 0;
let snapshot = INITIAL_SNAPSHOT;
const listeners = new Set<() => void>();
const replacedListeners = new Set<() => void>();

let resolveReady!: () => void;
let readyPromise = createReadyPromise();
let isReady = false;

let startFlight: Promise<void> | null = null;
let syncFlight: Promise<boolean> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let channel: BroadcastChannel | null = null;
let unsubscribeSessionEnded: (() => void) | null = null;
let unsubscribeBrowserEvents: (() => void) | null = null;

function createReadyPromise(): Promise<void> {
    return new Promise<void>((resolve) => {
        resolveReady = resolve;
    });
}

function markReady(): void {
    if (isReady) return;
    isReady = true;
    resolveReady();
}

function setSnapshot(patch: Partial<AuthSnapshot>): void {
    snapshot = { ...snapshot, ...patch };
    setAuthStatus(snapshot.status);
    for (const listener of [...listeners]) listener();
}

function invalidResponse(): ApiError {
    return new ApiError({ status: 200, code: "invalid_response", message: API_ERROR_MESSAGES.invalidResponse });
}

function scheduleProactiveRefresh(accessExpiresAt: string | undefined): void {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;

    const expiresAt = accessExpiresAt ? Date.parse(accessExpiresAt) : Number.NaN;
    if (Number.isNaN(expiresAt)) return;

    // Часы клиента могут расходиться с сервером: таймер — только оптимизация, основной механизм — refresh по 401.
    const delay = expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS;
    if (delay < MIN_PROACTIVE_REFRESH_DELAY_MS) return;

    refreshTimer = setTimeout(
        () => {
            refreshTimer = undefined;
            syncWithServer().then(
                (ok) => {
                    if (!ok) notifySessionEnded();
                },
                // Сеть недоступна: не конец сессии, следующий запрос получит 401 и повторит refresh.
                () => undefined,
            );
        },
        Math.min(delay, MAX_TIMER_DELAY_MS),
    );
}

function applyAuthorized(response: IsAuthorizedResponse): void {
    if (
        !response.isAuthorized ||
        !response.accessToken ||
        !response.email ||
        !Number.isSafeInteger(response.userId) ||
        !response.userId ||
        response.userId < 0 ||
        typeof response.displayName !== "string"
    )
        throw invalidResponse();

    const previous = snapshot.user;
    const user: AuthUser = {
        email: response.email,
        isSuperuser: response.isSuperuser === true,
        userId: response.userId,
        displayName: response.displayName,
    };
    // Другая учётная запись (вход или сброс пароля поверх открытой сессии): данные прежней не должны остаться.
    if (previous && (previous.userId !== user.userId || previous.email.toLowerCase() !== user.email.toLowerCase())) {
        contextVersion++;
        for (const listener of [...replacedListeners]) listener();
    }

    accessToken = response.accessToken;
    tokenVersion++;
    scheduleProactiveRefresh(response.accessExpiresAt);
    setSnapshot({ status: "authenticated", user, startupError: null, endReason: null });
    markReady();
}

function clearSession(endReason: SessionEndReason | null): void {
    contextVersion++;
    accessToken = null;
    tokenVersion++;
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
    setSnapshot({ status: "anonymous", user: null, startupError: null, endReason });
    markReady();
}

function broadcast(type: AuthTabMessage["type"]): void {
    const message = { type, at: Date.now() } satisfies AuthTabMessage;
    try {
        if (channel) {
            channel.postMessage(message);
            return;
        }
    } catch {
        // A blocked/closed channel falls back to a non-secret storage event.
    }
    try {
        window.localStorage.setItem(AUTH_STORAGE_EVENT, JSON.stringify(message));
        window.localStorage.removeItem(AUTH_STORAGE_EVENT);
    } catch {
        // Storage can also be unavailable; revalidate on focus in this browser.
    }
}

function isTabMessage(data: unknown): data is AuthTabMessage {
    if (typeof data !== "object" || data === null) return false;
    const { type, at } = data as Record<string, unknown>;
    return (type === "token-updated" || type === "session-changed" || type === "logout") && typeof at === "number";
}

function handleTabMessage(data: unknown): void {
    if (!isTabMessage(data)) return;

    if (data.type === "session-changed") {
        clearSession(null);
        notifySessionEnded();
        isReady = false;
        readyPromise = createReadyPromise();
        setSnapshot({ status: "unknown" });
        // A refresh started before the cookie changed cannot restore the previous account.
        void (syncFlight ?? Promise.resolve()).catch(() => {}).then(() => retryAuthSession());
        startFlight = null;
        return;
    }

    if (data.type === "logout") {
        if (snapshot.status === "anonymous") return;
        clearSession("logout");
        notifySessionEnded();
        return;
    }

    // Другая вкладка вошла или обновила cookie. Токен по каналу не передаётся: вкладка без сессии
    // делает свой `isauthorized` под блокировкой. Вкладки с сессией не реагируют — иначе refresh одной
    // вызывал бы refresh во всех; их токен при необходимости обновит 401.
    if (snapshot.status === "anonymous") {
        syncWithServer().catch(() => undefined);
    }
}

function openChannel(): void {
    if (!unsubscribeBrowserEvents && typeof window !== "undefined") {
        const storage = (event: StorageEvent) => {
            if (event.key !== AUTH_STORAGE_EVENT || !event.newValue) return;
            try {
                handleTabMessage(JSON.parse(event.newValue));
            } catch {
                /* Ignore unrelated storage values. */
            }
        };
        const focus = () => {
            if (!channel) void syncWithServer().catch(() => undefined);
        };
        window.addEventListener("storage", storage);
        window.addEventListener("focus", focus);
        unsubscribeBrowserEvents = () => {
            window.removeEventListener("storage", storage);
            window.removeEventListener("focus", focus);
        };
    }
    if (channel || typeof BroadcastChannel === "undefined") return;
    try {
        channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
        channel.onmessage = (event: MessageEvent<unknown>) => handleTabMessage(event.data);
    } catch {
        channel = null;
    }
}

/**
 * `isauthorized` с записью результата в сессию. Single-flight во вкладке и под блокировкой между вкладками.
 * `true` — есть токен, `false` — сессии нет. Ошибка сети или 5xx пробрасывается: это не конец сессии.
 */
function syncWithServer(): Promise<boolean> {
    syncFlight ??= (async () => {
        const versionBefore = tokenVersion;
        try {
            return await withRefreshLock(async () => {
                // Пока ждали блокировку, токен вкладки сменился (вход, сброс пароля, выход) — он свежее.
                if (tokenVersion !== versionBefore) return accessToken !== null;

                const response = await authApi.isAuthorized();
                if (tokenVersion !== versionBefore) return accessToken !== null;

                if (!response.isAuthorized) {
                    clearSession(snapshot.status === "authenticated" ? "expired" : null);
                    return false;
                }
                applyAuthorized(response);
                broadcast("token-updated");
                return true;
            });
        } finally {
            syncFlight = null;
        }
    })();
    return syncFlight;
}

const bridge: AuthBridge = {
    ready: () => readyPromise,
    getAccessToken: () => accessToken,
    getContextKey: () => contextVersion,
    refresh: syncWithServer,
};

/**
 * Первый `isauthorized` вкладки; повторные вызовы возвращают тот же промис (StrictMode, несколько провайдеров).
 * Только в браузере: вызывается из эффекта `AuthProvider`.
 */
export function startAuthSession(): Promise<void> {
    if (!unsubscribeSessionEnded) {
        connectAuth(bridge);
        openChannel();
        // Повторный 401 в HTTP-клиенте: refresh вернул токен, но сервер его не принял.
        unsubscribeSessionEnded = onSessionEnded(() => {
            if (snapshot.status === "authenticated") clearSession("expired");
        });
    }

    startFlight ??= syncWithServer().then(
        () => undefined,
        (error: unknown) => {
            startFlight = null;
            // Сессию уже установил вход или сброс пароля — ошибка старта не важна.
            if (isReady) return;
            setSnapshot({ startupError: isApiError(error) ? error : ApiError.network(error) });
        },
    );
    return startFlight;
}

/** Повтор первого `isauthorized` после ошибки сети («Сервер недоступен» → «Повторить»). */
export function retryAuthSession(): Promise<void> {
    if (snapshot.startupError) setSnapshot({ startupError: null });
    return startAuthSession();
}

/**
 * Записать сессию из ответа `login`, `register`, `password-reset/confirm`
 * и сообщить другим вкладкам `session-changed`.
 */
export function establishSession(response: IsAuthorizedResponse): void {
    applyAuthorized(response);
    broadcast("session-changed");
}

export async function loginWithPassword(body: LoginRequest): Promise<void> {
    await withRefreshLock(async () => establishSession(await authApi.login(body)));
}

export async function registerUser(body: RegisterRequest): Promise<void> {
    await withRefreshLock(async () => establishSession(await authApi.register(body)));
}

/** Новый пароль по токену из письма: пользователь сразу входит, текущая сессия (если была) заменяется. */
export async function confirmPasswordReset(body: PasswordResetConfirmRequest): Promise<void> {
    establishSession(await authApi.confirmPasswordReset(body));
}

/**
 * Выход: сервер отзывает сеанс, затем удаляется токен из памяти и очищаются кэши.
 * Ошибка сети сохраняет сеанс для повтора. Событие `logout` завершает сеанс в соседних вкладках.
 */
export async function logoutSession(): Promise<void> {
    try {
        if (accessToken) await authApi.logout();
    } catch (error) {
        if (!isApiError(error) || error.status !== 401) throw error;
    }
    endAccountSession();
}

/** Server already revoked this account's sessions (password change/account deletion). */
export function endAccountSession(): void {
    clearSession("logout");
    broadcast("logout");
    notifySessionEnded();
}

export function getAuthSnapshot(): AuthSnapshot {
    return snapshot;
}

export function getServerAuthSnapshot(): AuthSnapshot {
    return INITIAL_SNAPSHOT;
}

export function subscribeAuthSession(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Сессию заменила другая учётная запись: кэш прежней нужно очистить. */
export function onSessionReplaced(listener: () => void): () => void {
    replacedListeners.add(listener);
    return () => {
        replacedListeners.delete(listener);
    };
}

/** Сброс модуля между тестами (вместе с `resetAuthBridgeForTests`). */
export function resetAuthSessionForTests(): void {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
    channel?.close();
    channel = null;
    unsubscribeSessionEnded?.();
    unsubscribeSessionEnded = null;
    unsubscribeBrowserEvents?.();
    unsubscribeBrowserEvents = null;
    accessToken = null;
    tokenVersion = 0;
    contextVersion = 0;
    snapshot = INITIAL_SNAPSHOT;
    listeners.clear();
    replacedListeners.clear();
    readyPromise = createReadyPromise();
    isReady = false;
    startFlight = null;
    syncFlight = null;
}
